import React, { useState, useEffect, useCallback } from 'react';
import { ChevronRight, Edit, Trash2, AlertTriangle, Loader, Pill, Users, Calendar, Link as LinkIcon, Plus, X, ExternalLink } from 'lucide-react';
import ApiService from './ApiService';

const MedicationDetailPage = ({ medication, config, onBack, onMedicationDeleted, onMedicationUpdated }) => {
  const [patientsUsingMedication, setPatientsUsingMedication] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showAddLink, setShowAddLink] = useState(false);
  const [newLink, setNewLink] = useState({ title: '', url: '' });
  const [savingLink, setSavingLink] = useState(false);

  const t = config?.terminology || {
    item: { singular: 'Medication', plural: 'Medications', singularLower: 'medication', pluralLower: 'medications' },
    client: { singular: 'Patient', plural: 'Patients', singularLower: 'patient', pluralLower: 'patients' }
  };

  const loadPatientsUsingMedication = useCallback(async () => {
    try {
      setLoading(true);
      const data = await ApiService.checkMedicationAssignments(medication.medicationId);
      setPatientsUsingMedication(data.patients || []);
    } catch (err) {
      console.error('Failed to load patients:', err);
      setPatientsUsingMedication([]);
    } finally {
      setLoading(false);
    }
  }, [medication.medicationId]);

  useEffect(() => {
    loadPatientsUsingMedication();
  }, [loadPatientsUsingMedication]);

  const handleDeleteMedication = async () => {
    setDeleting(true);
    try {
      await ApiService.deleteMedication(medication.medicationId);
      onMedicationDeleted(medication.medicationId);
      onBack();
    } catch (err) {
      console.error('Failed to delete medication:', err);
      alert('Failed to delete medication. Please try again.');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleAddLink = async () => {
    if (!newLink.title.trim() || !newLink.url.trim()) {
      alert('Please provide both title and URL');
      return;
    }

    // Validate URL format
    try {
      new URL(newLink.url);
    } catch (e) {
      alert('Please provide a valid URL (e.g., https://youtube.com/watch?v=...)');
      return;
    }

    setSavingLink(true);
    try {
      const links = medication.links || [];
      links.push({
        id: Date.now().toString(),
        title: newLink.title.trim(),
        url: newLink.url.trim(),
        addedAt: new Date().toISOString()
      });

      await ApiService.updateMedication(medication.medicationId, { links });
      
      // Update local medication object
      medication.links = links;
      if (onMedicationUpdated) {
        onMedicationUpdated({ ...medication, links });
      }

      setNewLink({ title: '', url: '' });
      setShowAddLink(false);
    } catch (err) {
      console.error('Failed to add link:', err);
      alert('Failed to add link. Please try again.');
    } finally {
      setSavingLink(false);
    }
  };

  const handleDeleteLink = async (linkId) => {
    if (!window.confirm('Remove this link?')) return;

    try {
      const links = (medication.links || []).filter(link => link.id !== linkId);
      await ApiService.updateMedication(medication.medicationId, { links });
      
      medication.links = links;
      if (onMedicationUpdated) {
        onMedicationUpdated({ ...medication, links });
      }
    } catch (err) {
      console.error('Failed to delete link:', err);
      alert('Failed to delete link. Please try again.');
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'GLP-1': 'bg-spruce-100 text-spruce-800',
      'Longevity': 'bg-green-100 text-green-800',
      'Metabolic': 'bg-spruce-100 text-spruce-800',
      'Cardiovascular': 'bg-red-100 text-red-800',
      'Hormone': 'bg-pink-100 text-pink-800',
      'Vitamin': 'bg-yellow-100 text-yellow-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  if (!medication) return null;

  const medicationLinks = medication.links || [];

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <button onClick={onBack} className="hover:text-spruce-600">
          {t.item.plural}
        </button>
        <ChevronRight size={16} />
        <span>{medication.name}</span>
      </div>

      {/* Medication Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-start">
          <div className="flex items-start gap-4">
            <div className="p-4 bg-spruce-100 rounded-lg">
              <Pill className="text-spruce-600" size={32} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">{medication.name}</h2>
              <div className="flex gap-3 mt-2">
                {medication.category && (
                  <span className={`px-3 py-1 rounded-full text-sm ${getCategoryColor(medication.category)}`}>
                    {medication.category}
                  </span>
                )}
                <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm">
                  {medication.dosageUnit}
                </span>
              </div>
              {medication.description && (
                <p className="mt-3 text-gray-600">{medication.description}</p>
              )}
              {medication.notes && (
                <div className="mt-3 p-3 bg-spruce-50 border border-spruce-200 rounded">
                  <p className="text-sm text-spruce-900">
                    <strong>Administration Notes:</strong> {medication.notes}
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <button className="flex items-center gap-2 px-4 py-2 text-spruce-600 hover:bg-spruce-50 rounded-lg transition">
              <Edit size={18} />
              Edit
            </button>
            <button 
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition"
            >
              <Trash2 size={18} />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Educational Links Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <LinkIcon className="text-spruce-600" size={20} />
            <h3 className="text-lg font-semibold text-gray-800">Educational Links</h3>
          </div>
          <button
            onClick={() => setShowAddLink(true)}
            className="flex items-center gap-2 px-4 py-2 bg-spruce-600 text-white rounded-lg hover:bg-spruce-700 transition"
          >
            <Plus size={18} />
            Add Link
          </button>
        </div>

        {medicationLinks.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            No educational links added yet. Add YouTube videos, articles, or other resources for patients.
          </p>
        ) : (
          <div className="space-y-3">
            {medicationLinks.map((link) => (
              <div
                key={link.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
              >
                <div className="flex items-center gap-3 flex-1">
                  <LinkIcon className="text-gray-400" size={18} />
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-800">{link.title}</h4>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-spruce-600 hover:text-spruce-800 flex items-center gap-1"
                    >
                      {link.url}
                      <ExternalLink size={12} />
                    </a>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteLink(link.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                  title="Remove link"
                >
                  <X size={18} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 p-3 bg-spruce-50 border border-spruce-200 rounded">
          <p className="text-sm text-spruce-900">
            💡 <strong>Note:</strong> When assigning this {t.item.singularLower} to a {t.client.singularLower}, 
            you'll have the option to include these links in reminder messages.
          </p>
        </div>
      </div>

      {/* Add Link Modal */}
      {showAddLink && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Add Educational Link</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Link Title
                </label>
                <input
                  type="text"
                  value={newLink.title}
                  onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
                  placeholder="e.g., How to administer injection"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL
                </label>
                <input
                  type="url"
                  value={newLink.url}
                  onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                  placeholder="https://youtube.com/watch?v=..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Paste a YouTube video, article, or any educational resource URL
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddLink(false);
                  setNewLink({ title: '', url: '' });
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleAddLink}
                disabled={savingLink}
                className="flex-1 px-4 py-2 bg-spruce-600 text-white rounded-lg hover:bg-spruce-700 disabled:opacity-50 transition"
              >
                {savingLink ? 'Adding...' : 'Add Link'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Patients Using This Medication */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center gap-2 mb-4">
          <Users className="text-gray-600" size={20} />
          <h3 className="text-lg font-semibold text-gray-800">
            {t.client.plural} Using This {t.item.singular}
          </h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader className="animate-spin text-spruce-600" size={24} />
          </div>
        ) : patientsUsingMedication.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            No {t.client.pluralLower} are currently assigned this {t.item.singularLower}.
          </p>
        ) : (
          <div className="divide-y divide-gray-200">
            {patientsUsingMedication.map((patient) => (
              <div key={patient.patientId} className="py-3 flex items-center justify-between hover:bg-gray-50 px-3 rounded">
                <div>
                  <p className="font-medium text-gray-800">{patient.name}</p>
                  <p className="text-sm text-gray-500">
                    Assigned {new Date(patient.assignedAt).toLocaleDateString()}
                  </p>
                </div>
                <Calendar className="text-gray-400" size={18} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Delete {t.item.singular}?</h3>
            </div>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete <strong>{medication.name}</strong>?
            </p>
            {patientsUsingMedication.length > 0 && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-sm text-yellow-900">
                  <strong>{patientsUsingMedication.length} {t.client.pluralLower}</strong> are currently using this medication. 
                  Their existing schedules will be preserved but you won't be able to assign this medication to new patients.
                </p>
              </div>
            )}
            <p className="text-red-600 font-semibold mb-6">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button
                onClick={handleDeleteMedication}
                disabled={deleting}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50 transition flex items-center justify-center gap-2"
              >
                {deleting && <Loader className="animate-spin" size={16} />}
                Delete {t.item.singular}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedicationDetailPage;